// v0.33 build 36 (R1): the expense bucket displays its category's CURRENT name via
// lookup — finance_subtypes[subtype]?.name ?? subtype — scoped to type='Expense' rows
// ONLY. Non-expense subtypes (Sale/Purchase rows carry lowercase item categories like
// 'feed'/'eggs') must NEVER hit the expense map: the scope avoids any collision with
// the expense ids ('feed' ≠ 'Feed' today, and the scope keeps it safe forever).
export function subtypesMap(list) {
  return Object.fromEntries((list || []).map(s => [s.id, s.name]));
}

export function bucketLabel(map, type, subtype) {
  if (!subtype || type !== 'Expense') return subtype;
  return (map && map[subtype]) || subtype;
}

// v0.33 build 52 (R2): consumption AUTO rows carry type='Consumption' + the lowercase
// item category constant as subtype ('feed'/'medicine'). Display the
// item_category_labels LABEL for that constant — the mirror of the expense live-name
// lookup, scoped by row type so the two maps can never collide. When a constant has
// several labels, the first active one (by created order) wins; a label-less constant
// falls back to the raw lowercase string.
export function categoryLabelMap(rows) {
  const m = {};
  for (const r of rows || []) {
    if (r.is_active === false) continue;
    if (m[r.category] === undefined) m[r.category] = r.name;
  }
  return m;
}

// One resolver for any journal/report row: expense -> finance_subtypes live name,
// consumption/mortality -> item_category_labels label (build 57: a Mortality row's
// 'birds' subtype displays as Birds00 too), everything else -> the raw subtype.
// 810d — OTHER REVENUE joins the finance_subtypes arm. A 798 revenue bucket is a
// finance_subtypes row exactly like an expense bucket (TEXT primary key, so the leg's
// `subtype` IS the bucket id), and `subtypesMap` already carries every row regardless of
// applies_to — so the name was always in the map; only the type scope kept it out. Without
// this, the General Journal and Reports render the raw id ("eggcrate_scrap_sale") where
// Settings says "Egg-crate scrap sale".
//
// 832 (B4) — THE TRANSITIONAL 'Payment' ARM IS RETIRED, exactly as 810d said it would be. That arm
// existed only because an Other Revenue leg carried type='Payment' by inheritance (809 built the tab
// on FinanceForm, whose FINANCE_TYPES is the single-element ['Payment']). 832 gives Other Revenue its
// own operation type: db/482 adds the operation_types row, widens finance_type_check by that ONE
// value and REPOINTS the one live leg (OP-20260816-114245-L01) to type='Other Revenue', and
// DataEntryPage's buildFinancePayload now WRITES that type. So no leg reaches this helper as
// 'Payment' with a bucket id any more, and keeping the arm would only risk the collision the build-36
// comment above warns about: a genuine Payment subtype that happens to match a finance_subtypes id
// would be relabelled. OBSERVED 2026-08-16 AFTER db/482: the live Payment-leg subtypes are Supplier
// Payment 13, Other 133, refund_collection 2, blank 2 — and NONE of them is a finance_subtypes id, so
// dropping the arm changes NO rendered label anywhere today. The 'Other Revenue' arm carries the
// bucket names from here on.
// 810g — PROSE for process subtypes. A subtype that resolves to neither a finance_subtypes bucket nor
// an item-category label is a PROCESS literal, and the seed/writers spell those in snake_case
// ('landed_cost_absorption', 'refund_collection', 'writeoff'). They sat raw beside already-prose
// siblings ('Supplier Payment', 'Inventory Transfer', 'Opening'), so one list read as two vocabularies.
//
// Prettifies ONLY when the value looks machine-written — starts lowercase, or contains an underscore.
// An already-prose literal ('Supplier Payment', 'Other', 'Production') matches neither test and is
// returned untouched, so nothing that already read well is rewritten.
//
// This is the LAST fallback: buckets and category labels resolve first and never reach it. It is
// exported so the Subtype COLUMN and the subtype FILTER share one definition — 810d/810e/810f exist
// because those two drifted apart, and a second copy here would reopen exactly that.
export function prettyProcess(subtype) {
  if (!subtype || typeof subtype !== 'string') return subtype;
  const machineWritten = subtype.includes('_') || /^[a-z]/.test(subtype);
  if (!machineWritten) return subtype;
  const spaced = subtype.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function subtypeDisplay(bucketsMap, catsMap, type, subtype) {
  if (!subtype) return subtype;
  if (type === 'Expense' || type === 'Other Revenue')
    return (bucketsMap && bucketsMap[subtype]) || prettyProcess(subtype);
  // 810e — SALE and PURCHASE join the category arm. Their legs carry the same lowercase item-category
  // constant as Consumption/Mortality ('eggs'/'birds'/'feed'/'medicine'/'equipment'), so they were
  // rendering raw ("Sale / eggs") next to a Consumption row already showing "Feed00" — the same
  // vocabulary, displayed two ways on one screen. build 52 added Consumption, build 57 added Mortality;
  // Sale/Purchase were simply never added.
  // Purchase also carries PROCESS subtypes that are NOT categories — cancellation, writeoff,
  // landed_cost, landed_cost_absorption, supplier_credit, refund_receivable. Those are absent from
  // catsMap, so `|| subtype` leaves them raw, which is correct: they name a process, not a category.
  if (type === 'Consumption' || type === 'Mortality' || type === 'Sale' || type === 'Purchase')
    return (catsMap && catsMap[subtype]) || prettyProcess(subtype);
  return prettyProcess(subtype);
}

// GES-191: roll a P&L account's bucket sublines up by expense group (finance_subtypes.parent_group = the group name, one level).
// groupOf(subtype) -> label | null. A group sits where its first member sat; ungrouped buckets stay flat.
// Subtotals are sums of the SAME sublines, so grouped and flat always foot to the same account amount.
export function rollUpSublines(sublines, groupOf) {
  const out = [], seen = {};
  for (const sl of sublines || []) {
    const g = groupOf(sl.subtype);
    if (!g) { out.push({ kind: 'line', sl }); continue; }
    if (!seen[g]) { seen[g] = { kind: 'group', group: g, amount: 0, ids: [], children: [] }; out.push(seen[g]); }
    const r = seen[g];
    r.amount += Number(sl.amount) || 0; r.ids.push(...(sl.ids || [])); r.children.push(sl);
  }
  return out;
}
